import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ScatterText.css';

gsap.registerPlugin(ScrollTrigger);

const TITLE_LINES = ['a creative mind turned builder.'];
const COVER_HOLD_SCATTER_DELAY = 0.4;
const COVER_HOLD_TIDY_PORTION = 0.35;
const DEFAULT_SCROLL_TIDY_DISTANCE = '+=24%';

type ScatterCharacter = {
  id: string;
  value: string;
  isSpace: boolean;
  style?: React.CSSProperties;
};

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

const buildScatterCharacters = (): ScatterCharacter[][] =>
  TITLE_LINES.map((line, lineIndex) =>
    Array.from(line).map((char, charIndex) => {
      if (char === ' ') {
        return {
          id: `${lineIndex}-${charIndex}`,
          value: '\u00A0',
          isSpace: true,
        };
      }

      return {
        id: `${lineIndex}-${charIndex}`,
        value: char,
        isSpace: false,
        style: {
          ['--char-x' as string]: randomBetween(-3.8, 3.8).toFixed(3),
          ['--char-y' as string]: randomBetween(-62, 62).toFixed(3),
          ['--char-z' as string]: randomBetween(-10, 4).toFixed(3),
          ['--char-rx' as string]: randomBetween(-9, 9).toFixed(3),
          ['--char-ry' as string]: randomBetween(-12, 12).toFixed(3),
          ['--char-rz' as string]: randomBetween(-8, 8).toFixed(3),
          ['--char-scale' as string]: randomBetween(0.86, 1.12).toFixed(3),
          ['--char-opacity' as string]: randomBetween(0.66, 1).toFixed(3),
          ['--char-blur' as string]: randomBetween(0.25, 2.3).toFixed(3),
        },
      };
    })
  );

export const ScatterText: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const scatterLines = useMemo(buildScatterCharacters, []);

  const playAudio = useCallback(async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    try {
      await audio.play();
    } catch {}
  }, []);

  const handleAudioToggle = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    const nextMuted = !isAudioMuted;
    audio.muted = nextMuted;
    setIsAudioMuted(nextMuted);

    if (!nextMuted && audio.paused) {
      await playAudio();
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) {
      return;
    }

    const pointerX = ((event.clientX - bounds.left) / bounds.width - 0.5) * -1.4;
    const pointerY = ((event.clientY - bounds.top) / bounds.height - 0.5) * -1.4;

    container.style.setProperty('--hero-pointer-x', `${pointerX.toFixed(4)}%`);
    container.style.setProperty('--hero-pointer-y', `${pointerY.toFixed(4)}%`);
  };

  const handlePointerLeave = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.style.setProperty('--hero-pointer-x', '0%');
    container.style.setProperty('--hero-pointer-y', '0%');
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const root = containerRef.current;
    const heroSection =
      (root.closest('.scatter-screen') as HTMLElement | null) ?? root;
    const coverStage = root.closest<HTMLElement>('[data-cover-stage]');
    const useCoverStage =
      Boolean(coverStage) && window.matchMedia('(min-width: 810px)').matches;
    const holdRatio = Number(coverStage?.dataset.coverHold ?? 0);
    const tidyTrigger = useCoverStage && coverStage ? coverStage : heroSection;
    const coverTidyEndShare = COVER_HOLD_SCATTER_DELAY + COVER_HOLD_TIDY_PORTION;

    const setProgress = (progress: number) => {
      const nextProgress = gsap.utils.clamp(0, 1, progress);
      const scatterFactor = Math.max(1 - nextProgress * 3, 0);

      root.style.setProperty('--scatter-progress', nextProgress.toFixed(4));
      root.style.setProperty('--scatter-factor', scatterFactor.toFixed(4));
    };

    setProgress(0);

    const tidyTriggerInstance = ScrollTrigger.create({
      trigger: tidyTrigger,
      start: 'top top',
      end:
        useCoverStage && holdRatio > 0
          ? () => `+=${window.innerHeight * holdRatio * coverTidyEndShare}`
          : DEFAULT_SCROLL_TIDY_DISTANCE,
      scrub: 1,
      invalidateOnRefresh: true,
      onUpdate: (instance) => {
        if (useCoverStage && holdRatio > 0) {
          const coverTime = instance.progress * coverTidyEndShare;
          const effectiveProgress = gsap.utils.clamp(
            0,
            1,
            (coverTime - COVER_HOLD_SCATTER_DELAY) / COVER_HOLD_TIDY_PORTION
          );
          setProgress(effectiveProgress);
          return;
        }

        setProgress(instance.progress);
      },
    });

    return () => {
      tidyTriggerInstance.kill();
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.volume = 0.45;
    audio.muted = isAudioMuted;

    if (!isAudioMuted) {
      void playAudio();
    }
  }, [isAudioMuted, playAudio]);

  useEffect(() => {
    if (isAudioMuted) {
      return undefined;
    }

    const resumeAfterInteraction = (event: PointerEvent | KeyboardEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest('.scatter-audio-toggle')
      ) {
        return;
      }

      if (audioRef.current?.paused) {
        void playAudio();
      }
    };

    window.addEventListener('pointerdown', resumeAfterInteraction);
    window.addEventListener('keydown', resumeAfterInteraction);

    return () => {
      window.removeEventListener('pointerdown', resumeAfterInteraction);
      window.removeEventListener('keydown', resumeAfterInteraction);
    };
  }, [isAudioMuted, playAudio]);

  return (
    <div
      ref={containerRef}
      className="scatter-text-container"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <div className="scatter-video-frame" aria-hidden="true">
        <video
          className="scatter-hero-video"
          poster="/videos/moon-garden-hero-poster.jpg"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          disablePictureInPicture
          tabIndex={-1}
        >
          <source src="/videos/moon-garden-hero.mp4" type="video/mp4" />
        </video>
      </div>
      <audio
        ref={audioRef}
        src="/audio/moon-garden-soundtrack.mp4"
        autoPlay
        loop
        muted={isAudioMuted}
        preload="auto"
        onCanPlay={() => void playAudio()}
        onLoadedData={() => void playAudio()}
        aria-hidden="true"
      />
      {typeof document !== 'undefined'
        ? createPortal(
            <button
              className="scatter-audio-toggle"
              type="button"
              onClick={handleAudioToggle}
              aria-label={isAudioMuted ? 'Unmute' : 'Mute'}
              aria-pressed={!isAudioMuted}
              title={isAudioMuted ? 'Unmute' : 'Mute'}
            >
              {isAudioMuted ? (
                <SpeakerSlash size={12} weight="regular" aria-hidden="true" />
              ) : (
                <SpeakerHigh size={12} weight="regular" aria-hidden="true" />
              )}
            </button>,
            document.body
          )
        : null}
      <div className="scatter-text" aria-label={TITLE_LINES.join(' ')}>
        {scatterLines.map((line, lineIndex) => (
          <span key={`line-${lineIndex}`} className="scatter-title-line">
            {line.map((char) => (
              <span
                key={char.id}
                className={char.isSpace ? 'scatter-char scatter-char--space' : 'scatter-char'}
                style={char.style}
              >
                {char.value}
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
};
